import React, { useMemo, useState, useCallback, useEffect } from "react";
import { Icon } from "@iconify/react";
import "../Accounts/ledger-page.css";
import { PAGE_SIZE_OPTIONS } from "../../constants/pageSizeOptions";
import SearchableSelect from "../Common/SearchableSelect";
import useApi from "../../hook/useApi";
import Paggination from "../Paggination";

const DATE_PLACEHOLDER = "dd/mm/yyyy, --:-- --";

const SortHint = () => (
  <Icon
    icon="tabler:chevron-down"
    className="icon ms-1 text-secondary align-middle"
    style={{ fontSize: "0.65rem" }}
  />
);

const EmptyChartIcon = () => (
  <div className="opacity-40 mb-3" style={{ width: "4rem", height: "4rem" }}>
    <svg viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M8 56H56"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-secondary"
      />
      <path
        d="M12 48V40M22 48V32M32 48V24M42 48V16M52 48V28"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        className="text-secondary"
      />
      <path
        d="M12 30L22 20L32 28L52 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-secondary"
      />
    </svg>
  </div>
);

const TradeEditDeleteLogLayer = () => {
  const { AsyncGetApiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [logType, setLogType] = useState("update");
  const [rows, setRows] = useState([]);
  const [pageMeta, setPageMeta] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [filters, setFilters] = useState({
    segment: "",
    script: "",
    client: "",
    fromDate: "",
    toDate: "",
    showAdminOnly: true,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [limitMode, setLimitMode] = useState("all");

  const limitVal = limitMode === "all" ? 1000 : Number(limitMode);
  const tableData = rows;

  const filteredRows = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return tableData;

    return tableData.filter((row) => {
      return (
        row.symbol?.toLowerCase().includes(q) ||
        row.userId?.name?.toLowerCase().includes(q) ||
        row.userId?.loginId?.toLowerCase().includes(q) ||
        row.changedBy?.name?.toLowerCase().includes(q) ||
        row.action?.toLowerCase().includes(q)
      );
    });
  }, [rows, searchTerm]);

  const total = pageMeta.total;
  const hasPrev = pageMeta.hasPrevPage;
  const hasNext = pageMeta.hasNextPage;

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleApply = () => {
    console.log("Applying filters:", { logType, ...filters });
  };

  const handleClear = () => {
    setFilters({
      segment: "",
      script: "",
      client: "",
      fromDate: "",
      toDate: "",
      showAdminOnly: true,
    });
  };

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const payload = { pageNo, limitVal };

      if (searchTerm.trim()) {
        payload.search = searchTerm.trim();
      }

      const result = await AsyncGetApiCall("/orders/trade-logs", payload);

      if (result?.success) {
        setRows(Array.isArray(result.data) ? result.data : []);
        setPageMeta({
          total: Number(result.total) || 0,
          totalPages: Number(result.totalPages) || 0,
          currentPage: Number(result.currentPage) || pageNo,
          hasNextPage: Boolean(result.hasNextPage),
          hasPrevPage: Boolean(result.hasPrevPage),
        });
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Failed to fetch edit logs", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [AsyncGetApiCall, pageNo, limitVal, searchTerm]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-GB");
  };

  const getStatusBadge = (status) => {
    const s = (status || "").toLowerCase();

    if (s === "cancelled")
      return <span className="badge bg-danger">Cancelled</span>;

    if (s === "completed")
      return <span className="badge bg-success">Completed</span>;

    return <span className="badge bg-secondary">{status}</span>;
  };

  const startEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + 1;
  const endEntry =
    total === 0 ? 0 : (pageMeta.currentPage - 1) * limitVal + filteredRows.length;

  return (
    <>
      <h5 className="fw-bold text-primary-light text-uppercase mb-12">
        Trade Edit/Delete Log
      </h5>
      <div className="d-flex align-items-center gap-2 text-secondary-light mb-24">
        <Icon icon="solar:home-smile-angle-outline" className="icon text-lg" />
        <span className="text-secondary-light">-</span>
        <span className="text-secondary-light">Home</span>
        <Icon icon="tabler:chevron-right" className="icon text-sm" />
        <span className="fw-semibold text-primary-light">
          Trade Edit/Delete Log
        </span>
      </div>

      <div className="card border shadow-sm radius-16 mb-24 ledger-filter-card">
        <div className="card-body p-24">
          <div className="d-flex flex-wrap align-items-center gap-4 gap-lg-5 mb-24">
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="radio"
                name="log_type"
                id="log-type-update"
                checked={logType === "update"}
                onChange={() => setLogType("update")}
              />
              <label
                className="form-check-label fw-medium text-primary-light"
                htmlFor="log-type-update"
              >
                Update
              </label>
            </div>
            <div className="form-check mb-0">
              <input
                className="form-check-input"
                type="radio"
                name="log_type"
                id="log-type-delete"
                checked={logType === "delete"}
                onChange={() => setLogType("delete")}
              />
              <label
                className="form-check-label fw-medium text-primary-light"
                htmlFor="log-type-delete"
              >
                Delete
              </label>
            </div>
          </div>

          <div className="row g-4 align-items-end">
            <div className="col-12 col-md-6 col-xl-4 col-xxl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Segment
              </label>
              <select
                name="segment"
                value={filters.segment}
                onChange={handleFilterChange}
                className="form-select radius-12 h-40-px"
              >
                <option value="">Select segment</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-xl-4 col-xxl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Select script
              </label>
              <select
                name="script"
                value={filters.script}
                onChange={handleFilterChange}
                className="form-select radius-12 h-40-px"
              >
                <option value="">Select script</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-xl-4 col-xxl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                Select client
              </label>
              <select
                name="client"
                value={filters.client}
                onChange={handleFilterChange}
                className="form-select radius-12 h-40-px"
              >
                <option value="">Select user</option>
              </select>
            </div>
            <div className="col-12 col-md-6 col-xl-4 col-xxl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                From date
              </label>
              <div className="position-relative">
                <input
                  type="date"
                  name="fromDate"
                  placeholder={DATE_PLACEHOLDER}
                  value={filters.fromDate}
                  onChange={handleFilterChange}
                  className="form-control radius-12 h-40-px pe-40"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-xl-4 col-xxl-2">
              <label className="form-label fw-bold text-primary-light mb-8 d-block text-uppercase small">
                To date
              </label>
              <div className="position-relative">
                <input
                  type="date"
                  name="toDate"
                  placeholder={DATE_PLACEHOLDER}
                  value={filters.toDate}
                  onChange={handleFilterChange}
                  className="form-control radius-12 h-40-px pe-40"
                />
              </div>
            </div>
            <div className="col-12 col-md-6 col-xl-4 col-xxl-2 d-flex align-items-center pb-1">
              <div className="form-check mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  name="showAdminOnly"
                  id="show-admin-only"
                  checked={filters.showAdminOnly}
                  onChange={handleFilterChange}
                />
                <label
                  className="form-check-label fw-medium text-primary-light"
                  htmlFor="show-admin-only"
                >
                  Show admin only
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex flex-wrap align-items-center gap-2 mt-24">
            <button
              type="button"
              onClick={handleApply}
              className="btn btn-sm radius-8 px-24 py-10 btn-ledger-apply"
            >
              Filter results
            </button>
            <button
              type="button"
              onClick={handleClear}
              className="btn btn-sm radius-8 px-24 py-10 btn-ledger-clear"
            >
              Clear filters
            </button>
          </div>
        </div>
      </div>

      <div className="card border shadow-sm radius-16 ledger-table-card">
        <div className="card-body p-24 pb-0 position-relative" style={{ zIndex: 10 }}>
          <div className="d-flex flex-column flex-md-row align-items-stretch align-items-md-center justify-content-between gap-3 mb-16">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="text-md fw-medium text-secondary-light mb-0">
                Show
              </span>
              <div style={{ minWidth: "96px" }}>
                <SearchableSelect
                  placeholder="Show"
                  value={limitMode}
                  showSearch={false}
                  onChange={(e) => setLimitMode(e.target.value)}
                  options={[
                    { label: "All", value: "all" },
                    ...PAGE_SIZE_OPTIONS.map((n) => ({
                      label: String(n),
                      value: String(n),
                    })),
                  ]}
                />
              </div>
            </div>
            <div
              className="position-relative flex-grow-1"
              style={{ maxWidth: "280px" }}
            >
              <Icon
                icon="ion:search-outline"
                className="icon position-absolute top-50 start-0 translate-middle-y ms-12 text-secondary-light"
              />
              <input
                type="search"
                className="form-control ps-40 h-40-px radius-12"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="table-responsive px-16 pb-16">
          <table className="table bordered-table sm-table mb-0 text-nowrap">
            <thead>
              <tr className="bg-neutral-100">
                <th className="fw-bold text-primary-light">
                  SR <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Change by <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Date time <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Client <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Script <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Price <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Lot <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Qty <SortHint />
                </th>
                <th className="fw-bold text-primary-light">
                  Status <SortHint />
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={9} className="text-center py-4">
                    Loading...
                  </td>
                </tr>
              ) : filteredRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-5">
                    <div className="d-flex flex-column align-items-center justify-content-center py-4">
                      <EmptyChartIcon />
                      <span className="text-secondary-light fw-medium">
                        No data available in table
                      </span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRows.map((row, index) => (
                  <tr key={row._id}>
                    {/* SR */}
                    <td>{index + 1}</td>

                    {/* Changed By */}
                    <td>
                      {row.changedBy?.name || "-"}
                      <div className="text-xs text-secondary">
                        {row.changedByModel}
                      </div>
                    </td>

                    {/* Date */}
                    <td>{formatDate(row.createdAt)}</td>

                    {/* Client */}
                    <td>
                      {row.userId?.name}
                      <div className="text-xs text-secondary">
                        {row.userId?.loginId}
                      </div>
                    </td>

                    {/* Script */}
                    <td>
                      {row.symbol}
                      <div className="text-xs text-secondary">
                        {row.segment}
                      </div>
                    </td>

                    {/* Price */}
                    <td>
                      <div>Old: {row.oldPrice}</div>
                      <div className="text-success">New: {row.newPrice}</div>
                    </td>

                    {/* Lot */}
                    <td>
                      <div>Old: {row.oldLot}</div>
                      <div className="text-success">New: {row.newLot}</div>
                    </td>

                    {/* Qty */}
                    <td>
                      <div>Old: {row.oldQty}</div>
                      <div className="text-success">New: {row.newQty}</div>
                    </td>

                    {/* Status */}
                    <td>
                      {getStatusBadge(row.newStatus)}
                      <div className="text-xs text-secondary">{row.action}</div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>

          <Paggination
            total={total}
            totalPages={pageMeta.totalPages}
            currentPage={pageMeta.currentPage}
            setPageNo={setPageNo}
            startFrom={startEntry}
            endTo={endEntry}
          />
        </div>
      </div>
    </>
  );
};

export default TradeEditDeleteLogLayer;
